import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import { PlusOutlined, ReloadOutlined, HeartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import DashboardSection from '../../features/dashboard/components/DashboardSection';
import { normalizeCareProgramListRow, PROGRAM_STATUS_COLORS } from './careProgramNormalize';
import { canManageCarePrograms, canViewCareProgramUtilization } from './careProgramPermissions';
import { lookupMemberScopeLabel, resolveMemberScopeOptions } from './careProgramEligibility';

const { Text } = Typography;

const STATUS_FILTER_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
  { label: 'All', value: 'all' },
];

const formatSeason = (row) => {
  const from = row?.active_from;
  const to = row?.active_to;
  if (!from && !to) return 'Always';
  if (from && to) return `${from} → ${to}`;
  if (from) return `From ${from}`;
  return `Until ${to}`;
};

const AdminCareProgramsList = ({ embedded = false, onOpenProgram, onCreateProgram }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();

  const canDesign = useMemo(
    () => canManageCarePrograms(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const canViewUtilization = useMemo(
    () => canViewCareProgramUtilization(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [scopeSchema, setScopeSchema] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const parsed = lib.parseApiEnvelope(await lib.getAdminCareProgramSchema());
        if (parsed.ok) setScopeSchema(parsed.data ?? parsed.body);
      } catch {
        // Non-fatal
      }
    })();
  }, []);

  const memberScopeOptions = useMemo(
    () => resolveMemberScopeOptions(scopeSchema),
    [scopeSchema],
  );

  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter === 'archived') {
        params.status = 'archived';
      }
      const parsed = lib.parseApiEnvelope(await lib.listAdminCarePrograms(params));
      if (!parsed.ok) {
        setError(parsed.message || 'Unable to load care programs');
        setPrograms([]);
        return;
      }
      let list = Array.isArray(parsed.data) ? parsed.data : [];
      if (statusFilter === 'active') {
        list = list.filter((row) => (row?.status ?? row?.program?.status) !== 'archived');
      }
      setPrograms(list.map(normalizeCareProgramListRow));
    } catch (e) {
      setError(e?.message || 'Network error');
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const columns = [
    {
      title: 'Program',
      key: 'name',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.display_name || row.program_code || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{row.program_code}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s) => <Tag color={PROGRAM_STATUS_COLORS[s] || 'default'}>{s || 'draft'}</Tag>,
    },
    {
      title: 'Encounter type',
      dataIndex: 'checkin_type_id',
      width: 120,
      render: (id) => (id ? `#${id}` : '—'),
    },
    {
      title: 'Eligibility',
      key: 'eligibility',
      width: 200,
      render: (_, row) => {
        const label = lookupMemberScopeLabel(row.member_scope, memberScopeOptions);
        const isOpen = row.member_scope === 'all_members';
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{label}</Text>
            {isOpen && (
              <Text type="warning" style={{ fontSize: 11 }}>Includes all enrollees</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Season',
      key: 'season',
      width: 180,
      render: (_, row) => <Text type="secondary" style={{ fontSize: 12 }}>{formatSeason(row)}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_, row) => (
        <Space size={4}>
          {canDesign ? (
            <Button type="link" onClick={() => (onOpenProgram ? onOpenProgram(String(row.program_id)) : navigate(`/plans/care-programs/${row.program_id}`))}>
              Edit
            </Button>
          ) : (
            <Button type="link" onClick={() => (onOpenProgram ? onOpenProgram(String(row.program_id)) : navigate(`/plans/care-programs/${row.program_id}`))}>
              View
            </Button>
          )}
          {row.status === 'published' && canViewUtilization && (
            <Button
              type="link"
              onClick={() => (onOpenProgram ? onOpenProgram(String(row.program_id), 'performance') : navigate(`/plans/care-programs/${row.program_id}?tab=performance`))}
            >
              Performance
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const headerActions = (
    <>
      <Segmented
        size="small"
        options={STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
      />
      <Button size="small" icon={<ReloadOutlined />} onClick={fetchPrograms} disabled={loading}>
        Refresh
      </Button>
      {canDesign && (
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => (onCreateProgram ? onCreateProgram() : navigate('/plans/care-programs/new'))}
        >
          New program
        </Button>
      )}
    </>
  );

  const body = (
    <>
      <Alert
        type="info"
        showIcon
        message="Employer benefits built on encounter types"
        description="Each program links to a clinical encounter type. Publish, then enable per organization. HR utilization reports only enrollees on active paid policies, narrowed by eligibility."
      />
      {error && <Alert type="error" message={error} showIcon />}
      <Spin spinning={loading}>
        <Table
          rowKey="program_id"
          columns={columns}
          dataSource={programs}
          pagination={{ pageSize: 20 }}
          locale={{
            emptyText: statusFilter === 'archived'
              ? 'No archived programs'
              : 'No care programs yet',
          }}
        />
      </Spin>
    </>
  );

  if (embedded) {
    return (
      <DashboardSection
        id="plans-care-programs"
        icon={<HeartOutlined />}
        title="Care programs"
        subtitle={`${programs.length} program${programs.length === 1 ? '' : 's'}`}
        collapsible={false}
        variant="content"
        action={headerActions}
      >
        {body}
      </DashboardSection>
    );
  }

  return (
    <div style={{ padding: embedded ? 0 : undefined }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!embedded && (
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <Typography.Title level={3} style={{ margin: 0 }}>Care Programs</Typography.Title>
            <Space>{headerActions}</Space>
          </Space>
        )}
        {body}
      </Space>
    </div>
  );
};

export default AdminCareProgramsList;
